import React, { useState, useEffect } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/constants";

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-600"}`}
      />
    ))}
  </div>
);

const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const ReviewCard = ({ review }) => (
  <Card className="bg-[#0A0A0A] border-white/10 hover:border-[#00E676]/30 transition-all h-full">
    <CardContent className="p-6">
      <div className="flex items-start gap-4 mb-4">
        {review.profile_photo_url ? (
          <img 
            src={review.profile_photo_url} 
            alt={review.author_name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`w-12 h-12 rounded-full bg-gradient-to-br from-[#00E676] to-[#00C853] items-center justify-center text-black font-bold flex-shrink-0 ${review.profile_photo_url ? 'hidden' : 'flex'}`}
        >
          {getInitials(review.author_name)}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white truncate">{review.author_name}</h4>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={review.rating} />
            <span className="text-xs text-gray-500">{review.relative_time_description}</span>
          </div>
        </div>
      </div>
      <Quote className="w-6 h-6 text-[#00E676]/30 mb-2" />
      <p className="text-gray-300 text-sm leading-relaxed line-clamp-4">
        {review.text}
      </p>
    </CardContent>
  </Card>
);

const GoogleReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/google-reviews/digital-media-ninja`);
      
      if (!response.ok) {
        throw new Error("Failed to fetch reviews");
      }
      
      const data = await response.json();
      
      if (data.reviews && data.reviews.length > 0) {
        setReviews(data.reviews);
        setPlaceInfo(data.place_info);
        setIsLive(true);
      } else {
        setReviews([]);
        setIsLive(false);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviews([]);
      setIsLive(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  if (loading || reviews.length === 0) return null;

  const displayReviews = reviews.slice(0, 6);
  const rating = placeInfo?.rating || 5.0;
  const totalReviews = placeInfo?.total_reviews;
  const googleUrl = placeInfo?.google_url || "https://g.page/digitalmedianinja";

  return (
    <section className="section-padding bg-black" data-testid="google-reviews-section">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/10 mb-6">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-sm text-[#00E676] font-medium">Google Reviews</span>
            {isLive && (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Live
              </span>
            )}
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Trusted by <span className="text-[#00E676]">Dallas Businesses</span>
          </h2>
          
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`w-6 h-6 ${i < Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-600"}`} 
                />
              ))}
            </div>
            <span className="text-2xl font-bold text-white">{rating.toFixed(1)}</span>
            {totalReviews ? <span className="text-gray-400">Based on {totalReviews} reviews</span> : null}
          </div>
          
          <p className="text-gray-400 text-lg">
            See what Dallas business owners say about working with Digital Media Ninja
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {displayReviews.map((review, index) => (
            <ReviewCard key={review.id || index} review={review} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="btn-outline group" data-testid="view-all-reviews-btn">
              View All Google Reviews
              <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
